/**
 * Ereignisse — der Verlauf, ohne Ausnahme.
 *
 * In cubic projects existierte ein Aktivitätsverlauf, und die Hausregel „jede
 * Mutation schreibt einen Aktivitätseintrag" stand in der Betriebsanleitung.
 * Was fehlte, war die Erzwingung: Fünf Vorgänge tauchten im Verlauf nicht auf,
 * weil sie an der Konvention vorbeigeschrieben wurden. Solange ein Verlauf nur
 * angezeigt wird, sind solche Lücken kosmetisch. Sobald Regeln darauf laufen
 * sollen, sind sie disqualifizierend — eine Automation auf einem lückenhaften
 * Strom ist unzuverlässig, ohne es zu zeigen.
 *
 * Hier ist der Verlauf kein Nebenprodukt, sondern die Bedingung: Der
 * Schreibpfad im Server nimmt keine Änderung an, zu der kein Ereignis gehört
 * (siehe `apps/server/src/db/schreiben.ts`). Diese Datei liefert das
 * Vokabular, gegen das dort geprüft wird.
 */
import { z } from "zod";
import { knotenSchema, objektartSchema, beziehungsartSchema } from "./kanten.js";
/* -------------------------------------------------------------------------
 * Wer handelt
 * ---------------------------------------------------------------------- */
export const AKTEURSARTEN = ["person", "metarr", "mcp", "system"];
/**
 * Der Handelnde.
 *
 * `metarr` und `mcp` tragen **zusätzlich** die Person, in deren Namen sie
 * handeln. Ohne diese Person gibt es keinen Werkzeugaufruf — die Rechtegrenze
 * hängt daran, und sie ist damit im Verlauf nachvollziehbar statt behauptet.
 * Nur `system` darf ohne Person auftreten (Zeitpläne, Migrationen).
 */
export const akteurSchema = z
    .object({
    art: z.enum(AKTEURSARTEN),
    personId: z.uuid().nullish(),
    /** Welcher Lauf, welches Rezept — bei `metarr` und `mcp` gesetzt. */
    laufId: z.uuid().nullish(),
})
    .superRefine((akteur, ctx) => {
    if (akteur.art !== "system" && !akteur.personId) {
        ctx.addIssue({
            code: "custom",
            path: ["personId"],
            message: "Nur das System handelt ohne Person. Metarr und MCP handeln immer im Namen einer Person.",
        });
    }
});
/* -------------------------------------------------------------------------
 * Was geschieht
 * ---------------------------------------------------------------------- */
export const VORGAENGE = [
    "angelegt",
    "commit_gespeichert",
    "geaendert",
    "umbenannt",
    "verschoben",
    "zugewiesen",
    "zuweisung_entfernt",
    "status_gewechselt",
    "verknuepft",
    "entknuepft",
    "kommentiert",
    "marke_gesetzt",
    "marke_geloest",
    "klaerung_gestellt",
    "klaerung_beantwortet",
    "vorschlag_angenommen",
    "vorschlag_abgelehnt",
    "archiviert",
    "wiederhergestellt",
    "geloescht",
];
export const vorgangSchema = z.enum(VORGAENGE);
export const VORGANG_LABELS = {
    angelegt: "angelegt",
    commit_gespeichert: "gespeichert",
    geaendert: "geändert",
    umbenannt: "umbenannt",
    verschoben: "verschoben",
    zugewiesen: "zugewiesen",
    zuweisung_entfernt: "Zuweisung entfernt",
    status_gewechselt: "Status gewechselt",
    verknuepft: "verknüpft",
    entknuepft: "Verknüpfung entfernt",
    kommentiert: "kommentiert",
    marke_gesetzt: "Textstelle markiert",
    marke_geloest: "Textstelle aufgelöst",
    klaerung_gestellt: "Klärung gestellt",
    klaerung_beantwortet: "Klärung beantwortet",
    vorschlag_angenommen: "Vorschlag angenommen",
    vorschlag_abgelehnt: "Vorschlag abgelehnt",
    archiviert: "archiviert",
    wiederhergestellt: "wiederhergestellt",
    geloescht: "gelöscht",
};
/* -------------------------------------------------------------------------
 * Das Ereignis
 * ---------------------------------------------------------------------- */
/**
 * Wie lang ein Feldwert im Verlauf höchstens sein darf.
 *
 * Die Zahl steht hier und nur hier: Schreibpfad und Lesepfad müssen dieselbe
 * Grenze kennen. Liefen sie auseinander, entstünden Ereignisse, die geschrieben
 * werden können und beim Lesen die ganze Seite kippen — genau das ist passiert,
 * bis ein einziger Merge-Request-Text den Ereignisstrom zweier Projekte
 * unlesbar machte.
 */
export const FELDWERT_HOECHSTLAENGE = 2000;
/** Die Marke, an der ein gekürzter Wert erkennbar ist. */
export const FELDWERT_GEKUERZT = " […]";
/**
 * Kürzt einen Feldwert auf die zulässige Länge und macht das sichtbar.
 *
 * Ein Verlauf ist eine Zusammenfassung, kein Archiv: Wer den vollen Text
 * braucht, findet ihn am Objekt selbst. Abschneiden ist deshalb richtig —
 * still abschneiden wäre es nicht, denn dann sähe ein halber Text aus wie ein
 * ganzer.
 */
export function kuerzeFeldwert(wert) {
    if (wert === null || wert.length <= FELDWERT_HOECHSTLAENGE)
        return wert;
    return (wert.slice(0, FELDWERT_HOECHSTLAENGE - FELDWERT_GEKUERZT.length) +
        FELDWERT_GEKUERZT);
}
/**
 * Eine geänderte Eigenschaft. Vorher und nachher stehen als Text da — der
 * Verlauf soll lesbar sein, ohne das Schema zu kennen.
 */
export const feldaenderungSchema = z.object({
    feld: z.string().min(1).max(64),
    vorher: z.string().max(FELDWERT_HOECHSTLAENGE).nullable(),
    nachher: z.string().max(FELDWERT_HOECHSTLAENGE).nullable(),
});
export const ereignisSchema = z.object({
    vorgang: vorgangSchema,
    /** Woran es geschah. */
    betrifft: knotenSchema,
    /** Für Sichtbarkeit und Filterung. Nur beim Projekt selbst leer. */
    projektId: z.uuid().nullable(),
    akteur: akteurSchema,
    /** Was sich geändert hat — leer bei Vorgängen ohne Feldbezug. */
    aenderungen: z.array(feldaenderungSchema).max(50).default([]),
    /** Der zweite Endpunkt bei `verknuepft` / `entknuepft`. */
    gegenueber: knotenSchema.nullish(),
    beziehungsart: beziehungsartSchema.nullish(),
    /** Freier Zusatz, wenn der Vorgang ihn braucht — kurz halten. */
    hinweis: z.string().trim().max(1000).nullish(),
});
/* -------------------------------------------------------------------------
 * Regeln
 * ---------------------------------------------------------------------- */
/** Vorgänge, die ohne Feldänderungen sinnlos wären. */
const BRAUCHT_AENDERUNGEN = ["geaendert", "umbenannt"];
/** Vorgänge, die ein Gegenüber brauchen. */
const BRAUCHT_GEGENUEBER = ["verknuepft", "entknuepft"];
/**
 * Ist dieses Ereignis vollständig genug, um geschrieben zu werden?
 *
 * Diese Prüfung sitzt vor der Datenbank, nicht in der Oberfläche — sie gilt
 * damit für jeden Schreibweg gleichermaßen.
 */
export function pruefeEreignis(ereignis) {
    const maengel = [];
    if (BRAUCHT_AENDERUNGEN.includes(ereignis.vorgang) &&
        ereignis.aenderungen.length === 0) {
        maengel.push(`„${VORGANG_LABELS[ereignis.vorgang]}" ohne genannte Felder ist keine Auskunft.`);
    }
    if (BRAUCHT_GEGENUEBER.includes(ereignis.vorgang)) {
        if (!ereignis.gegenueber) {
            maengel.push("Eine Verknüpfung braucht beide Endpunkte.");
        }
        if (!ereignis.beziehungsart) {
            maengel.push("Eine Verknüpfung braucht ihre Art.");
        }
    }
    // Globale Objektarten (Person, Gruppe, Vorlage) und das Projekt selbst dürfen
    // ohne projektId stehen. Alles andere hängt an einem Projekt.
    const globalOhneProjekt = ereignis.betrifft.art === "projekt" ||
        ereignis.betrifft.art === "person" ||
        ereignis.betrifft.art === "gruppe" ||
        ereignis.betrifft.art === "vorlage";
    if (!globalOhneProjekt && ereignis.projektId === null) {
        maengel.push("Ohne Projekt lässt sich die Sichtbarkeit des Ereignisses nicht bestimmen.");
    }
    return maengel;
}
function beschreibeAkteur(art) {
    switch (art) {
        case "metarr":
            return "Metarr";
        case "system":
            return "das System";
        case "mcp":
            return "ein verbundenes Werkzeug";
        case "person":
            return "jemand";
        default:
            return "jemand";
    }
}
/**
 * Ein Ereignis für den Verlauf in einem Satz.
 *
 * Deckt alle bekannten Vorgangsarten; bei `geaendert` werden die Felder genannt.
 */
export function beschreibe(eintrag) {
    return beschreibeEreignis(eintrag);
}
/** Kanonischer Name aus Paket 12 — dieselbe Formulierung für UI, MCP und Metarr. */
export function beschreibeEreignis(eintrag) {
    const was = VORGANG_LABELS[eintrag.vorgang] ?? eintrag.vorgang;
    const wer = beschreibeAkteur(eintrag.akteur.art);
    const art = eintrag.betrifft.art;
    if (eintrag.gegenueber && eintrag.beziehungsart) {
        return `${wer} hat ${art} mit ${eintrag.gegenueber.art} ${was}.`;
    }
    if (eintrag.vorgang === "geaendert" && eintrag.aenderungen.length > 0) {
        const felder = eintrag.aenderungen.map((a) => a.feld).join(", ");
        return `${wer} hat an ${art} geändert: ${felder}.`;
    }
    if (eintrag.vorgang === "commit_gespeichert") {
        const sha = eintrag.hinweis?.startsWith("commit:")
            ? eintrag.hinweis.slice("commit:".length)
            : "";
        return `${wer} hat Commit ${sha} gespeichert.`;
    }
    if (eintrag.aenderungen.length > 0) {
        const felder = eintrag.aenderungen.map((a) => a.feld).join(", ");
        return `${wer} hat ${felder} ${was}.`;
    }
    return `${wer} hat ${art} ${was}.`;
}
/* -------------------------------------------------------------------------
 * Verlaufs-Cursor (Zeit + Kennung)
 * ---------------------------------------------------------------------- */
/**
 * Cursor aus Zeitpunkt und ID — stabil bei gleichem Zeitstempel.
 * Versioniertes Textformat, damit shared ohne Node-Krypto und Buffer bleibt.
 */
export function verlaufCursorEin(geschehenAm, id) {
    const zeit = geschehenAm instanceof Date ? geschehenAm : new Date(geschehenAm);
    if (Number.isNaN(zeit.getTime()) || !z.uuid().safeParse(id).success) {
        throw new Error("Ungültige Daten für einen Verlaufscursor.");
    }
    return `v1:${zeit.getTime()}:${id}`;
}
export function verlaufCursorAus(cursor) {
    const treffer = /^v1:(\d+):([0-9a-f-]+)$/i.exec(cursor);
    if (!treffer)
        return null;
    const millis = Number(treffer[1]);
    const id = treffer[2];
    if (!Number.isSafeInteger(millis) ||
        millis < 0 ||
        !z.uuid().safeParse(id).success) {
        return null;
    }
    const geschehenAm = new Date(millis);
    if (Number.isNaN(geschehenAm.getTime()))
        return null;
    return { geschehenAm, id };
}
export const verlaufCursorSchema = z
    .string()
    .min(1)
    .max(128)
    .refine((cursor) => verlaufCursorAus(cursor) !== null, {
    message: "Ungültiger Verlaufscursor.",
});
export { objektartSchema };
//# sourceMappingURL=ereignisse.js.map